import dayjs from 'dayjs';
import { ChevronDownIcon, TagIcon, UserXIcon } from 'lucide-react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreakpointBelow } from '~/hooks/use-breakpoints';
import { useRelativeDate } from '~/hooks/use-relative-date';
import { EntityAvatar } from '~/modules/common/entity-avatar';
import { HighlightText } from '~/modules/common/match-highlight';
import { TooltipButton } from '~/modules/common/tooltip-button';
import { useReadOnlyHide, useReadOnlyInert } from '~/modules/project/use-read-only';
import { useTaskCardStore } from '~/modules/task/card/task-card-store';
import type { DropdownsType } from '~/modules/task/dropdowns/types';
import { copyStartedTask } from '~/modules/task/helpers/copy-tasks';
import { handleTaskDropdownClick } from '~/modules/task/helpers/task-dropdown';
import { handleTaskSelect } from '~/modules/task/helpers/task-selection';
import { useTaskFieldHandlers } from '~/modules/task/hooks/use-task-field-handlers';
import { useTaskInteractionStore } from '~/modules/task/task-interaction-store';
import { statusOptionsByValue, TaskStatus } from '~/modules/task/task-properties';
import { statusButtonVariants } from '~/modules/task/task-styles';
import type { Task } from '~/modules/task/types';
import { AvatarGroup, AvatarGroupList, AvatarOverflowIndicator } from '~/modules/ui/avatar';
import { Badge } from '~/modules/ui/badge';
import { Button } from '~/modules/ui/button';
import { Checkbox } from '~/modules/ui/checkbox';
import { cn } from '~/utils/cn';

interface TaskCardFooterProps {
  task: Task;
  isSelected: boolean;
  isSheet?: boolean;
}

export const TaskCardFooter = memo(function TaskCardFooter({ task, isSelected, isSheet = false }: TaskCardFooterProps) {
  const { t } = useTranslation();
  const isMobile = useBreakpointBelow('sm');
  const readOnlyInert = useReadOnlyInert(task.projectId);
  const readOnlyHide = useReadOnlyHide(task.projectId);
  const labelsHide = task.labels.length ? '' : readOnlyHide;

  const taskState = useTaskCardStore((s) => s.states[task.id] ?? 'collapsed');
  // Task and label selection are mutually exclusive so the board header's remove button targets one kind
  const hasSelectedLabels = useTaskInteractionStore((s) => s.selectedLabelIds.length > 0);
  const isOtherProjectSelected = useTaskInteractionStore((s) => !!s.selectedProjectId && s.selectedProjectId !== task.projectId);
  // On mobile, expanded/editing cards move labels onto their own line above the footer,
  // freeing horizontal space to reveal the select checkbox and status dropdown sub-button.
  const isExpandedMobile = isMobile && taskState !== 'collapsed';

  const handlers = useTaskFieldHandlers(task);

  const statusChangedRelative = useRelativeDate(task.statusChangedAt);
  const isJustNow = dayjs().diff(dayjs.utc(task.statusChangedAt)) <= 1000;
  const isRelative = /^\d/.test(statusChangedRelative); // "2h", "5m" → use "ago"; "May 5" → don't
  const statusTooltip = isJustNow
    ? t('c:status_updated_just_now')
    : isRelative
      ? t('c:status_updated_at', { when: statusChangedRelative })
      : t('c:status_updated_on', { when: statusChangedRelative });

  const onDropdownOpen = (currentTarget: EventTarget & HTMLButtonElement, dropdownType: DropdownsType) => {
    const base = { triggerId: currentTarget.id, triggerRef: { current: currentTarget }, taskId: task.id };

    if (dropdownType === 'labels') {
      handleTaskDropdownClick({
        ...base,
        dropdownType,
        value: task.labels,
        projectId: task.projectId,
        onChange: handlers.onLabelsChange,
        // Collapse the selected section when the card already shows its selected labels.
        initialSelectedCollapsed: !isMobile || isExpandedMobile,
      });
    } else if (dropdownType === 'assignedTo') {
      handleTaskDropdownClick({ ...base, dropdownType, value: task.assignedTo, projectId: task.projectId, onChange: handlers.onAssignedToChange });
    } else if (dropdownType === 'status') {
      handleTaskDropdownClick({ ...base, dropdownType, value: task.status, onChange: handlers.onStatusChange });
    } else {
      handleTaskDropdownClick({
        ...base,
        dropdownType: 'primaryLabel',
        value: task.primaryLabelId,
        projectId: task.projectId,
        onChange: handlers.onPrimaryLabelChange,
      });
    }
  };

  const labelsButton = (
    <Button
      id={`labels-${task.id}${isSheet ? '-sheet' : ''}`}
      onClick={({ currentTarget }) => onDropdownOpen(currentTarget, 'labels')}
      aria-label={t('c:set_resource', { resource: t('c:label_other').toLowerCase() })}
      variant="ghost"
      size="xs"
      className={cn('relative flex h-auto min-h-8 min-w-8 px-0.5 py-0.5', labelsHide)}
      {...readOnlyInert}
    >
      {task.labels.length > 0 ? (
        isMobile && !isExpandedMobile ? (
          <div className="flex flex-wrap items-center gap-0.5 truncate font-normal text-[.75rem] leading-4">
            <span className="max-w-24 truncate px-1 lowercase">
              <HighlightText text={task.labels[0].name} />
            </span>
            {task.labels.length > 1 && <span className="px-1">+{task.labels.length - 1}</span>}
          </div>
        ) : (
          <div className="flex flex-wrap gap-0.5 truncate">
            {task.labels.map(({ name, id }) => {
              return (
                <div key={id} className="flex max-w-full flex-wrap items-center justify-center rounded-full px-0">
                  <Badge
                    variant="outline"
                    key={id}
                    className="inline-block min-h-4 max-w-full shrink truncate border-0 bg-transparent px-1 py-0 font-normal text-[.75rem] text-muted-foreground leading-4 shadow-none last:mr-0"
                  >
                    <HighlightText text={name} />
                  </Badge>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <TagIcon className="opacity-60" />
      )}
    </Button>
  );

  return (
    <div
      className={cn(
        'flex flex-col',
        isExpandedMobile && 'group-[.is-expanded]/task:fade-in group-[.is-expanded]/task:animate-in group-[.is-expanded]/task:duration-300',
      )}
    >
      {/* On mobile, show labels on their own line above the footer when expanded */}
      {isExpandedMobile && <div className={cn('mb-1 flex flex-row items-center', labelsHide)}>{labelsButton}</div>}
      <div className="group-[.is-expanded]/task:fade-in flex flex-row items-center group-[.is-expanded]/task:animate-in group-[.is-expanded]/task:duration-300 sm:gap-1">
        {!isSheet && (
          <Checkbox
            className={cn('mx-1 data-[state=checked]:border-primary', !isExpandedMobile && 'max-sm:hidden', readOnlyHide)}
            aria-label={t('c:select_resource', { resource: task.name })}
            checked={isSelected}
            disabled={hasSelectedLabels || isOtherProjectSelected}
            onCheckedChange={(checked) => handleTaskSelect(checked, task)}
          />
        )}
        {/* Labels are rendered above on mobile when expanded */}
        {!isExpandedMobile && labelsButton}
        <div className="ml-auto flex">
          <Button
            id={`assignedTo-${task.id}${isSheet ? '-sheet' : ''}`}
            onClick={({ currentTarget }) => onDropdownOpen(currentTarget, 'assignedTo')}
            aria-label={t('c:assign_to')}
            variant="ghost"
            size="xs"
            className="relative mr-1 flex min-w-8 justify-center px-1 opacity-80 group-hover/task:opacity-100 group-[.is-focused]/task:opacity-100"
            {...readOnlyInert}
          >
            {task.assignedTo.length > 0 ? (
              <AvatarGroup limit={isMobile ? 2 : 3}>
                <AvatarGroupList>
                  {task.assignedTo.map((user) => (
                    <EntityAvatar type="user" key={user.id} id={user.id} name={user.name} url={user.thumbnailUrl} className="h-6 w-6 text-xs" />
                  ))}
                </AvatarGroupList>
                <AvatarOverflowIndicator className="h-6 w-6 text-xs" />
              </AvatarGroup>
            ) : (
              <UserXIcon className="h-4 w-4 opacity-60" />
            )}
          </Button>

          <TooltipButton toolTipContent={statusTooltip} side="top">
            <Button
              onClick={() => {
                if (task.status === TaskStatus.Unstarted) copyStartedTask(task);
                handlers.onStatusChange(task.status - 1);
              }}
              disabled={task.status === TaskStatus.Accepted}
              variant="outlineGhost"
              size="xs"
              press={false}
              className={cn(
                'relative mr-1 font-normal disabled:opacity-100',
                !readOnlyHide && 'sm:rounded-r-none sm:border-r-0',
                !readOnlyHide && isExpandedMobile && 'rounded-r-none border-r-0',
                statusButtonVariants({ status: task.status }),
              )}
              {...readOnlyInert}
            >
              {t(`c:${readOnlyHide ? statusOptionsByValue[task.status].status : statusOptionsByValue[task.status].action}`)}
            </Button>
          </TooltipButton>
          <TooltipButton toolTipContent={statusTooltip} side="top">
            <Button
              id={`status-${task.id}${isSheet ? '-sheet' : ''}`}
              onClick={({ currentTarget }) => onDropdownOpen(currentTarget, 'status')}
              aria-label={t('c:set_resource', { resource: t('c:status').toLowerCase() })}
              variant="outlineGhost"
              size="xs"
              press={false}
              className={cn(
                'relative -ml-1 rounded-none rounded-r px-2 [&>svg]:transition-transform data-dropdowner-active:[&>svg]:rotate-180',
                !isExpandedMobile && 'max-sm:hidden',
                statusButtonVariants({ status: task.status }),
                readOnlyHide,
              )}
              {...readOnlyInert}
            >
              <ChevronDownIcon className="size-3.5" strokeWidth={2} />
            </Button>
          </TooltipButton>
        </div>
      </div>
    </div>
  );
});
